import {
  AI_BUILDER_MISSING_SLOT,
  DIFFICULTY_HINT_BY_CHOICE_LABEL,
  QUIZ_TYPE_HINT_BY_CHOICE_LABEL,
} from '../constants';

import {
  type AiBuilderQuizTypeHint,
  type AiBuilderSourceImportDecisionHint,
  type AiBuilderTurnHints,
} from './types';

const COUNT_PRESET_VALUES = new Set(['5', '10', '15', '20']);

const QUIZ_TYPE_HINT_VALUES: ReadonlySet<string> = new Set([
  'ox',
  'multiple',
  'subjective',
  'essay',
]);

/** 서버가 worksheet_import 질문의 options[].value 로 내려주는 결정 어휘. */
const SOURCE_IMPORT_DECISION_VALUES: ReadonlySet<string> = new Set([
  'import_source',
  'generate_new',
]);

type AiBuilderChoiceTurnHintOption = {
  label: string;
  value?: string;
};

type AiBuilderChoiceTurnHintMessage = {
  missingSlot?: string;
  options: AiBuilderChoiceTurnHintOption[];
};

/**
 * option 하나를 quiz-type hint 로 해석한다.
 * canonical value(label→value 계약)가 1차 신호이고, 라벨 매핑은 value 없이
 * 오던 구 계약 메시지(재열람된 이력 등) 하위호환 폴백이다.
 */
const getQuizTypeHint = (
  option: AiBuilderChoiceTurnHintOption,
): AiBuilderQuizTypeHint | undefined => {
  if (option.value && QUIZ_TYPE_HINT_VALUES.has(option.value)) {
    return option.value as AiBuilderQuizTypeHint;
  }

  return QUIZ_TYPE_HINT_BY_CHOICE_LABEL[option.label];
};

/**
 * 선택한 선지를 서버가 읽을 구조화 힌트로 변환한다.
 *
 * 선택은 **선지 객체**로 받는다 — 라벨은 로케일마다 달라지는 렌더 표면이라
 * 왕복 매칭 key 가 될 수 없다. 서버가 value 를 실어 보낸 슬롯
 * (worksheet_import·count·quiz_types)은 그 canonical value 를 그대로
 * 되돌려주고, 아직 value 가 없는 난이도만 라벨 매핑을 유지한다.
 */
export const createChoiceTurnHints = ({
  message,
  selectedOptions,
}: {
  message: AiBuilderChoiceTurnHintMessage;
  selectedOptions: AiBuilderChoiceTurnHintOption[];
}): AiBuilderTurnHints | undefined => {
  if (selectedOptions.length === 0) return undefined;

  if (
    message.missingSlot === AI_BUILDER_MISSING_SLOT.worksheetImport &&
    selectedOptions.length === 1
  ) {
    const value = selectedOptions[0].value;
    if (value && SOURCE_IMPORT_DECISION_VALUES.has(value)) {
      return {
        sourceImportDecision: value as AiBuilderSourceImportDecisionHint,
      };
    }
    return undefined;
  }

  if (
    message.missingSlot === AI_BUILDER_MISSING_SLOT.count &&
    selectedOptions.length === 1
  ) {
    const value = selectedOptions[0].value;
    if (value && COUNT_PRESET_VALUES.has(value)) {
      return { count: Number(value) };
    }
    return undefined;
  }

  // 유형·난이도는 전용 슬롯 없이 선지 구성으로 식별한다 — 일부만 해석되는
  // 혼합 선지를 유형/난이도 질문으로 오인하지 않도록 전 선지가 같은 축이어야 한다.
  const allOptionsAreQuizTypes = message.options.every(
    option => getQuizTypeHint(option) !== undefined,
  );

  if (allOptionsAreQuizTypes) {
    const quizTypes = selectedOptions
      .map(getQuizTypeHint)
      .filter(
        (quizType): quizType is AiBuilderQuizTypeHint => quizType !== undefined,
      );

    if (quizTypes.length === selectedOptions.length) {
      return { quizTypes };
    }
  }

  const allOptionsAreDifficulties = message.options.every(
    option => DIFFICULTY_HINT_BY_CHOICE_LABEL[option.label] !== undefined,
  );

  if (allOptionsAreDifficulties && selectedOptions.length === 1) {
    const difficulty =
      DIFFICULTY_HINT_BY_CHOICE_LABEL[selectedOptions[0].label];

    if (difficulty !== undefined) {
      return { difficulty };
    }
  }

  return undefined;
};
